/**
 * Cliente tRPC del workshop: `vault.*` / `vaultAdmin.*` (y el estado de MFA)
 * se responden desde el Vault de demo en memoria; todo lo demás va a la API
 * real, igual que el cliente global de lib/trpc.ts.
 *
 * Se monta en un `trpc.Provider` ANIDADO (con su propio QueryClient) dentro
 * del reproductor: las pantallas reales del Vault no cambian ni una línea, y
 * la caché de la demo nunca se mezcla con la del Vault de verdad.
 */
import { TRPCClientError, httpBatchLink, type TRPCLink } from '@trpc/client';
import { observable } from '@trpc/server/observable';
import superjson from 'superjson';
import type { AppRouter } from '../../../../.types/backend/src/routers';
import { authHeaders, trpc } from '@/lib/trpc';
import { handleDemo, isDemoHandled, type DemoState } from './demo-vault';

/** Latencia simulada para que los spinners y los "Guardando…" se vean como en la vida real. */
const LATENCY_MS = 220;

export function demoLink(state: DemoState): TRPCLink<AppRouter> {
  return () =>
    ({ op, next }) => {
      if (!isDemoHandled(op.path)) return next(op);
      return observable((observer) => {
        const timer = window.setTimeout(() => {
          try {
            const data = handleDemo(state, op.path, op.input);
            observer.next({ result: { type: 'data', data } });
            observer.complete();
          } catch (e) {
            observer.error(TRPCClientError.from(e as Error));
          }
        }, LATENCY_MS);
        return () => window.clearTimeout(timer);
      });
    };
}

export function createDemoTrpcClient(state: DemoState) {
  return trpc.createClient({
    links: [
      demoLink(state),
      httpBatchLink({
        url: `${import.meta.env.VITE_API_URL}/trpc`,
        headers: authHeaders,
        transformer: superjson,
      }),
    ],
  });
}
